import React, { useState, useEffect } from 'react';
import { Select } from 'antd';
import { User, UserPlus, Shield, Edit, Trash, Search, Plus } from 'lucide-react';
import { Input } from 'antd';
import { fetchUsers, addUser, updateUser, deleteUser, initializeAdmin } from '../utils/api';
import './Users.css';

const Users = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Form state for add/edit user
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    isActive: true,
    moduleAccess: {
      dashboard: true,
      createInvoice: true,
      adhocInvoice: true,
      createQuote: true,
      quoteHistory: true,
      clients: true,
      invoiceHistory: true,
      expenses: true,
      profitLoss: true,
      termSheets: true,
      vendors: true,
      payables: true,
      configurations: false,
      users: false
    }
  });

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'admin';

  // Load users from API
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await fetchUsers();
        setUsers(data);
      } catch (error) {
        // Error loading users
      } finally {
        setIsLoading(false);
      }
    };

    // Initialize admin user if no users exist
    const initializeAdminUser = async () => {
      try {
        await initializeAdmin();
      } catch (error) {
        // Admin initialization failed
      }
    };

    loadUsers();
    initializeAdminUser();
  }, []);

  // Add new user
  const handleAddUser = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const data = await addUser(formData);

      // Reload users list
      const usersData = await fetchUsers();
      setUsers(usersData);

      setShowAddModal(false);
      resetForm();
    } catch (error) {
      alert('Error creating user: ' + error.message);
    }
  };

  // Edit user
  const handleEditUser = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const data = await updateUser(selectedUser._id, formData);

      // Reload users list
      const usersData = await fetchUsers();
      setUsers(usersData);

      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
    } catch (error) {
      alert('Error updating user: ' + error.message);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (userId === currentUser.id || userId === currentUser._id) {
      alert('You cannot delete your own account');
      return;
    }

    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const data = await deleteUser(userId);

        // Reload users list
        const usersData = await fetchUsers();
        setUsers(usersData);
      } catch (error) {
        alert('Error deleting user: ' + error.message);
      }
    }
  };

  // Toggle user active status
  const handleToggleStatus = async (userId) => {
    if (userId === currentUser.id || userId === currentUser._id) {
      alert('You cannot deactivate your own account');
      return;
    }

    try {
      const user = users.find(u => u._id === userId);
      const data = await updateUser(userId, {
        ...user,
        isActive: !user.isActive
      });

      // Reload users list
      const usersData = await fetchUsers();
      setUsers(usersData);
    } catch (error) {
      alert('Error updating user status: ' + error.message);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
      isActive: true,
      moduleAccess: {
        dashboard: true,
        createInvoice: true,
        adhocInvoice: true,
        createQuote: true,
        quoteHistory: true,
        clients: true,
        invoiceHistory: true,
        expenses: true,
        profitLoss: true,
        termSheets: true,
        vendors: true,
        payables: true,
        configurations: false,
        users: false
      }
    });
  };

  // Open edit modal
  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      isActive: user.isActive,
      moduleAccess: user.moduleAccess || {
        dashboard: true,
        invoice: true,
        createQuote: true,
        quoteHistory: true,
        clients: true,
        invoiceHistory: true,
        expenses: true,
        profitLoss: true,
        termSheets: true,
        vendors: true,
        payables: true,
        configurations: false,
        users: false
      }
    });
    setShowEditModal(true);
  };

  // Handle module access change
  const handleModuleAccessChange = (module, checked) => {
    setFormData(prev => ({
      ...prev,
      moduleAccess: {
        ...prev.moduleAccess,
        [module]: checked
      }
    }));
  };

  // Handle role change - automatically set module access for admin
  const handleRoleChange = (role) => {
    if (role === 'admin') {
      setFormData(prev => ({
        ...prev,
        role,
        moduleAccess: {
          dashboard: true,
          invoice: true,
          createQuote: true,
          quoteHistory: true,
          clients: true,
          invoiceHistory: true,
          expenses: true,
          profitLoss: true,
          termSheets: true,
          vendors: true,
          payables: true,
          configurations: true,
          users: true
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        role,
        moduleAccess: {
          dashboard: true,
          invoice: true,
          createQuote: true,
          quoteHistory: true,
          clients: true,
          invoiceHistory: true,
          expenses: true,
          profitLoss: true,
          termSheets: true,
          vendors: true,
          payables: true,
          configurations: false,
          users: false
        }
      }));
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isAdmin) {
    return (
      <div className="users-container">
        <div className="access-denied">
          <Shield size={48} />
          <h2>Access Denied</h2>
          <p>You don't have permission to access the Users management page.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="users-container">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Users Management</h1>
          <p>Manage application users and their permissions</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            Add User
          </button>
        </div>
      </div>

      <div className="users-search">
        <div className="search-box">
          <Input
            prefix={<Search size={16} />}
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Module Access</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user._id || user.id} className={!user.isActive ? 'inactive' : ''}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'admin' ? <Shield size={12} /> : <User size={12} />}
                    {user.role}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {user.moduleAccess && Object.entries(user.moduleAccess)
                      .filter(([module, hasAccess]) => hasAccess)
                      .map(([module]) => {
                        const moduleNames = {
                          dashboard: 'Dashboard',
                          invoice: 'Invoice',
                          createInvoice: 'Invoice', // Backward compatibility
                          adhocInvoice: 'Invoice', // Backward compatibility
                          createQuote: 'Create Quote',
                          quoteHistory: 'Quote History',
                          termSheets: 'Term Sheets',
                          clients: 'Clients',
                          invoiceHistory: 'Invoice History',
                          expenses: 'Operational Cost',
                          profitLoss: 'Profit & Loss',
                          vendors: 'Vendors',
                          payables: 'Payable Invoices',
                          configurations: 'Configurations',
                          users: 'Users'
                        };
                        return moduleNames[module] || module;
                      })
                      .slice(0, 3)
                      .join(', ')}
                    {user.moduleAccess && Object.values(user.moduleAccess).filter(Boolean).length > 3 && '...'}
                  </div>
                </td>
                <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="action-btn edit"
                      onClick={() => openEditModal(user)}
                      title="Edit user"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      className="action-btn toggle"
                      onClick={() => handleToggleStatus(user._id)}
                      title={user.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {user.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    {user._id !== currentUser.id && user._id !== currentUser._id && (
                      <button
                        className="action-btn delete"
                        onClick={() => handleDeleteUser(user._id)}
                        title="Delete user"
                      >
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  Add New User
                </h2>
                <button 
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleAddUser}>
                <div className="form-group">
                  <label>Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.role}
                    onChange={(v) => handleRoleChange(v)}
                    options={[{ label: 'User', value: 'user' }, { label: 'Admin', value: 'admin' }, { label: 'Viewer (read-only)', value: 'viewer' }]}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      style={{ margin: 0, width: '16px', height: '16px' }}
                    />
                    <span>Active</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>Module Access {formData.role === 'admin' && <span style={{ color: '#666', fontSize: '12px' }}>(Admin users have access to all modules)</span>}</label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '12px',
                    marginTop: '8px'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.dashboard}
                        onChange={(e) => handleModuleAccessChange('dashboard', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Dashboard</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.invoice || formData.moduleAccess.createInvoice || formData.moduleAccess.adhocInvoice}
                        onChange={(e) => {
                          handleModuleAccessChange('invoice', e.target.checked);
                          // Remove old permissions if setting invoice
                          if (e.target.checked) {
                            const updated = { ...formData.moduleAccess, invoice: true };
                            delete updated.createInvoice;
                            delete updated.adhocInvoice;
                            setFormData({ ...formData, moduleAccess: updated });
                          }
                        }}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Invoice</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.createQuote}
                        onChange={(e) => handleModuleAccessChange('createQuote', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Create Quote</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.quoteHistory}
                        onChange={(e) => handleModuleAccessChange('quoteHistory', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Quote History</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.clients}
                        onChange={(e) => handleModuleAccessChange('clients', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Clients</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.invoiceHistory}
                        onChange={(e) => handleModuleAccessChange('invoiceHistory', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Invoice History</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.expenses}
                        onChange={(e) => handleModuleAccessChange('expenses', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Operational Cost</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.profitLoss}
                        onChange={(e) => handleModuleAccessChange('profitLoss', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Profit & Loss</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.termSheets}
                        onChange={(e) => handleModuleAccessChange('termSheets', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Term Sheets</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.configurations}
                        onChange={(e) => handleModuleAccessChange('configurations', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Configurations</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.users}
                        onChange={(e) => handleModuleAccessChange('users', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Users Management</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.vendors}
                        onChange={(e) => handleModuleAccessChange('vendors', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Vendors</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.payables}
                        onChange={(e) => handleModuleAccessChange('payables', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Payable Invoices</span>
                    </label>
                  </div>
                </div>

              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" onClick={handleAddUser}>
                  Add User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-right-overlay">
          <div className="modal-right-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-right-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#333' }}>
                  Edit User
                </h2>
                <button 
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#888', cursor: 'pointer', padding: 0 }}
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                    resetForm();
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="modal-right-body">
              <form onSubmit={handleEditUser}>
                <div className="form-group">
                  <label>Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password (leave blank to keep current)</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <Select
                    style={{ width: '100%' }}
                    value={formData.role}
                    onChange={(v) => handleRoleChange(v)}
                    options={[{ label: 'User', value: 'user' }, { label: 'Admin', value: 'admin' }, { label: 'Viewer (read-only)', value: 'viewer' }]}
                    getPopupContainer={(trigger) => trigger.parentNode}
                  />
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                      style={{ margin: 0, width: '16px', height: '16px' }}
                    />
                    <span>Active</span>
                  </label>
                </div>
                <div className="form-group">
                  <label>Module Access {formData.role === 'admin' && <span style={{ color: '#666', fontSize: '12px' }}>(Admin users have access to all modules)</span>}</label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '12px',
                    marginTop: '8px'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.dashboard}
                        onChange={(e) => handleModuleAccessChange('dashboard', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Dashboard</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.invoice || formData.moduleAccess.createInvoice || formData.moduleAccess.adhocInvoice}
                        onChange={(e) => {
                          handleModuleAccessChange('invoice', e.target.checked);
                          // Remove old permissions if setting invoice
                          if (e.target.checked) {
                            const updated = { ...formData.moduleAccess, invoice: true };
                            delete updated.createInvoice;
                            delete updated.adhocInvoice;
                            setFormData({ ...formData, moduleAccess: updated });
                          }
                        }}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Invoice</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.createQuote}
                        onChange={(e) => handleModuleAccessChange('createQuote', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Create Quote</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.quoteHistory}
                        onChange={(e) => handleModuleAccessChange('quoteHistory', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Quote History</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.clients}
                        onChange={(e) => handleModuleAccessChange('clients', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Clients</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.invoiceHistory}
                        onChange={(e) => handleModuleAccessChange('invoiceHistory', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Invoice History</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.expenses}
                        onChange={(e) => handleModuleAccessChange('expenses', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Operational Cost</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.profitLoss}
                        onChange={(e) => handleModuleAccessChange('profitLoss', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Profit & Loss</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.termSheets}
                        onChange={(e) => handleModuleAccessChange('termSheets', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Term Sheets</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.configurations}
                        onChange={(e) => handleModuleAccessChange('configurations', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Configurations</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.users}
                        onChange={(e) => handleModuleAccessChange('users', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Users Management</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.vendors}
                        onChange={(e) => handleModuleAccessChange('vendors', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Vendors</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: formData.role === 'admin' ? 'not-allowed' : 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.moduleAccess.payables}
                        onChange={(e) => handleModuleAccessChange('payables', e.target.checked)}
                        disabled={formData.role === 'admin'}
                        style={{ margin: 0, width: '16px', height: '16px', opacity: formData.role === 'admin' ? 0.6 : 1 }}
                      />
                      <span style={{ opacity: formData.role === 'admin' ? 0.6 : 1 }}>Payable Invoices</span>
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-right-footer">
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  resetForm();
                }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" onClick={handleEditUser}>
                  Update User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users; 